import { useCallback, useContext, useEffect, useState } from 'react';
import AuthContext from '../../context/authContext';
import useSocketIO from '../../hooks/useSocketIO';
import { isFeatureEnabled } from '../../config/featureFlags';
import { useCallProtocol } from '../../hooks/useCallProtocol';
import CallProtocolDialog from './CallProtocolDialog';

// Opens the call protocol as soon as the phone rings at this employee, so notes can be taken
// while the call runs. The backend pushes 'callProtocol:incoming' to the user's own socket room;
// a call handed over by the phone assistant arrives pre-filled, a direct call empty.
export default function CallProtocolListener() {
  const authCtx = useContext(AuthContext);
  const { on, off } = useSocketIO();
  const { getProtocol } = useCallProtocol();

  const [open, setOpen] = useState(false);
  const [protocol, setProtocol] = useState(null);
  const [caller, setCaller] = useState(null);
  const [aiContext, setAiContext] = useState(null);
  const [callContext, setCallContext] = useState(null);
  const [bookingContext, setBookingContext] = useState(null);

  const enabled = isFeatureEnabled('callProtocol') && authCtx.checkRight?.('callProtocol:write');

  const handleIncoming = useCallback(
    async payload => {
      if (!payload?.protocol) return;
      // Show the dialog immediately from the socket payload — the phone is ringing, waiting for a
      // request would defeat the purpose.
      setProtocol(payload.protocol);
      setCaller(payload.call?.caller || null);
      setAiContext(payload.aiContext || null);
      setCallContext(null);
      setBookingContext(null);
      setOpen(true);

      // Then fill in the call facts (time, branch, duration) the webhook payload does not carry.
      // Keeping that lookup out of the Comdesk webhook keeps the telephony path fast.
      try {
        const detail = await getProtocol(payload.protocol._id);
        if (detail?.callContext) setCallContext(detail.callContext);
        if (detail?.aiContext) setAiContext(detail.aiContext);
        if (detail?.bookingContext) setBookingContext(detail.bookingContext);
      } catch {
        // The dialog stays usable on the socket payload alone.
      }
    },
    [getProtocol]
  );

  useEffect(() => {
    if (!enabled) return;
    on('callProtocol:incoming', handleIncoming);
    return () => off('callProtocol:incoming', handleIncoming);
  }, [enabled, on, off, handleIncoming]);

  if (!enabled) return null;

  return (
    <CallProtocolDialog
      open={open}
      onClose={() => setOpen(false)}
      protocol={protocol}
      aiContext={aiContext}
      callContext={callContext}
      bookingContext={bookingContext}
      caller={caller}
    />
  );
}
